import { noop } from "neetocist";
import { Form } from "neetoui/formik";
import PropTypes from "prop-types";

import { INITIAL_FORM_VALUES } from "./constants";
import { EmailFormContext } from "./contexts/EmailForm";
import { getValidationSchema } from "./utils";

const EmailFormProvider = ({
  onSubmit = noop,
  initialValues = INITIAL_FORM_VALUES,
  children,
  formikProps,
  validationSchema: customValidations = {},
  showSendToField = false,
  showReplyToField = false,
  showSendToAsRadio = false,
  showCcField = false,
  showBccField = false,
  maxEmails = 10,
}) => (
  <div
    className="neeto-notification-email-outer-wrapper"
    data-testid="email-form-provider"
  >
    <EmailFormContext.Provider
      value={{
        showSendToField,
        showSendToAsRadio,
        showReplyToField,
        showCcField,
        showBccField,
      }}
    >
      <Form
        className="neeto-notification-email-inner-wrapper"
        formikProps={{
          enableReinitialize: true,
          validationSchema: getValidationSchema({
            showSendToField,
            showReplyToField,
            showSendToAsRadio,
            customValidations,
            maxEmails,
          }),
          initialValues: {
            ...initialValues,
            replyTo: initialValues.replyTo || globalProps.user.email,
          },
          onSubmit,
          ...formikProps,
        }}
      >
        {children}
      </Form>
    </EmailFormContext.Provider>
  </div>
);

EmailFormProvider.propTypes = {
  /**
   * A Boolean value that determines whether the '**sendTo**' field should be displayed or not.
   */
  showSendToField: PropTypes.bool,
  /**
   * A Boolean value that determines whether the '**replyTo**' field should be displayed or not.
   */
  showReplyToField: PropTypes.bool,
  /**
   * A Boolean value that determines whether the '**sendTo**' field should be radio type or not.
   */
  showSendToAsRadio: PropTypes.bool,
  /**
   * A function that will be triggered with input values when the 'Save changes' button is clicked.
   */
  onSubmit: PropTypes.func,
  /**
   * These are the predefined values that populate the form fields when the form is first rendered.
   */
  initialValues: PropTypes.shape({
    sendTo: PropTypes.array,
    sendToId: PropTypes.string,
    sendToCc: PropTypes.array,
    sendToBcc: PropTypes.array,
    replyTo: PropTypes.string,
    showCopyEmails: PropTypes.bool,
    subject: PropTypes.string,
    message: PropTypes.string,
  }),
  /**
   * A prop used for rendering either standard React element or a render prop function.
   */
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  /**
   * A prop that can be used to override the default Formik properties that are set within the component, providing customization flexibility.
   */
  formikProps: PropTypes.object,
  /**
   * A Yup object defining custom form validation rules, which will be merged with the default rules using the concat method.
   */
  validationSchema: PropTypes.object,
  /**
   * A prop to limit number of send to emails
   */
  maxEmails: PropTypes.number,
  /**
   * A Boolean value that determines whether the '**Cc**' field should be displayed or not when showSendToField is false.
   * This prop has no effect when showSendToField is true.
   */
  showCcField: PropTypes.bool,
  /**
   * A Boolean value that determines whether the '**Bcc**' field should be displayed or not when showSendToField is false.
   * This prop has no effect when showSendToField is true.
   */
  showBccField: PropTypes.bool,
};

export default EmailFormProvider;
